"use client";

import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useQueryClient } from "@tanstack/react-query";
import { useBlockableRouter } from "@/components/navigation-block";
import { Card, CardContent } from "@/components/ui/card";
import type { components } from "@/lib/api.gen";
import { AuthenticationForm } from "./components/authentication-form";
import { TwoFactorChallenge } from "./components/two-factor-challenge";

type SecondFactorMethod = components["schemas"]["SecondFactorMethod"];

export default function LoginPage() {
  const t = useTranslations();
  const router = useBlockableRouter();
  const queryClient = useQueryClient();
  const searchParams = useSearchParams();
  const next = searchParams.get("next") ?? "/";
  const oauthError = searchParams.get("oauth_error");
  // Set by the SSO callback when the account has an app-based second factor: the
  // primary factor (Google/Apple) is already proven server-side, so we resume
  // straight into the challenge here.
  const isOauth2fa = searchParams.get("oauth_2fa") === "1";

  const handleToggle = () => {
    const target = "/login/signup";
    if (next !== "/") {
      router.push(`${target}?next=${encodeURIComponent(next)}`);
      return;
    }
    router.push(target);
  };

  if (isOauth2fa) {
    const methods = (searchParams.get("methods")?.split(",").filter(Boolean) ??
      []) as SecondFactorMethod[];
    const onSuccess = async () => {
      await queryClient.invalidateQueries({ queryKey: ["user-summary"] });
      router.push(next);
    };
    return (
      <div className="flex items-center justify-center h-full">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <TwoFactorChallenge methods={methods} onSuccess={onSuccess} />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-full">
      <div className="flex w-full max-w-md flex-col gap-4">
        {oauthError && (
          <p className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-center text-sm text-destructive">
            {t("auth.oauth.error.generic")}
          </p>
        )}
        {next === "/event/new" && (
          <p className="rounded-md border bg-muted/50 p-3 text-center text-sm text-muted-foreground">
            {t("auth.event-create-hint")}
          </p>
        )}
        <AuthenticationForm authType="login" onToggleMethod={handleToggle} next={next} />
      </div>
    </div>
  );
}
